import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { API_BASE_URL } from "../utils/config";

/* Password Validator - Matches your Signup requirements */
function validatePassword(pw) {
  const hasMinLength = pw.length >= 8;
  const hasNumber = /\d/.test(pw);
  const hasUppercase = /[A-Z]/.test(pw);
  const hasLowercase = /[a-z]/.test(pw);
  const hasSymbol = /[^A-Za-z0-9]/.test(pw);
  
  return hasMinLength && hasNumber && hasUppercase && hasLowercase && hasSymbol;
}

function ResetPassword() {
  const location = useLocation();
  const navigate = useNavigate();
  const params = new URLSearchParams(location.search);

  /* --- STATE --- */
  const [email] = useState(params.get("email") || "");
  const [code, setCode] = useState("");
  const [password, setPassword] = useState("");
  const [retypePassword, setRetypePassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [passwordTouched, setPasswordTouched] = useState(false);
  
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!validatePassword(password)) {
      setError("Password does not meet requirements.");
      return;
    }

    if (password !== retypePassword) {
      setError("Passwords do not match.");
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/auth/confirmForgotPassword`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, confirmationCode: code, password }),
      });

      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(data.error || "Password reset failed");
      }

      // Success logic
      alert("Password reset successful! Please log in.");
      navigate("/login");
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-container">
      <div className="login-box">
        <h2>Set New Password</h2>

        {/* --- Error Message Display --- */}
        {error && <div className="error-banner">{error}</div>}

        <p style={{ fontSize: '14px', marginBottom: '15px', textAlign: 'center', color: '#64748b' }}>
          Check your email <strong>{email}</strong> for the verification code.
        </p>

        <form className="login-form" onSubmit={handleSubmit}>
          {/* Confirmation Code Input */}
          <input
            type="text"
            name="code"
            placeholder="Verification Code"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            required
          />

          {/* New Password Input */}
          <input
            type={showPassword ? "text" : "password"}
            name="password"
            placeholder="New Password"
            required
            value={password}
            onChange={(e) => {
              setPassword(e.target.value);
              setPasswordTouched(true);
            }}
            className={
              !passwordTouched ? "" : 
              validatePassword(password) ? "input-success" : "input-error"
            }
          />
          
          {/* Validation Hints - Matching Signup Style */}
          <div className="password-hints-inline">
            <span className={password.length >= 8 ? "done" : ""}>8+ chars</span>
            <span className={/[A-Z]/.test(password) ? "done" : ""}>Uppercase</span>
            <span className={/[a-z]/.test(password) ? "done" : ""}>Lowercase</span>
            <span className={/\d/.test(password) ? "done" : ""}>Number</span>
            <span className={/[^A-Za-z0-9]/.test(password) ? "done" : ""}>Symbol</span>
          </div>

          {/* Retype Password */}
          <input
            type={showPassword ? "text" : "password"}
            name="retypePassword"
            placeholder="Retype New Password"
            required
            value={retypePassword}
            onChange={(e) => {
              setRetypePassword(e.target.value);
              setPasswordTouched(true);
            }}
            className={
              !passwordTouched || !retypePassword ? "" : 
              (password === retypePassword) ? "input-success" : "input-error"
            }
          />

          {/* Show Password Toggle */}
          <div className="password-row">
            <span
              className="show-password-link"
              onClick={() => setShowPassword(p => !p)}
            >
              {showPassword ? "Hide password" : "Show password"}
            </span>
          </div>

          <button type="submit" disabled={loading}>
            {loading ? "Resetting..." : "Update Password"}
          </button>
        </form>

        <p>Return to <Link to="/login">Login</Link></p>
      </div>
    </div>
  );
}

export default ResetPassword;